/**
 * AUTH GATE - wrapper obsługujący stany autentykacji
 * Konwencja: Sikorski (H = si naturalne, B = si bemol)
 * 
 * Pokazuje odpowiedni UI w zależności od stanu:
 * - Ładowanie (z timeout 3s → "trwa dłużej")
 * - Błąd (z retry i nuke)
 * - Nie skonfigurowane
 * - Zalogowany (renderuje dzieci)
 */

import { useEffect, useState } from 'react'

import { useAuth } from '../hooks/useAuth'

interface AuthGateProps {
  children: React.ReactNode
}

export function AuthGate({ children }: AuthGateProps) {
  const { 
    isLoading, 
    error, 
    isConfigured, 
    retry, 
    nukeSessionAndReload 
  } = useAuth()
  
  // Śledzimy czy ładowanie trwa dłużej niż 3s (bez setInterval - redukuje render load)
  const [showLongLoading, setShowLongLoading] = useState(false)
  
  useEffect(() => {
    if (!isLoading) {
      setShowLongLoading(false)
      return
    }
    
    const timeout = setTimeout(() => {
      setShowLongLoading(true)
    }, 3000)
    
    return () => clearTimeout(timeout)
  }, [isLoading])

  // NIE skonfigurowane - błąd krytyczny
  if (!isConfigured) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="font-display text-xl text-burgundy mb-4">
            Błąd konfiguracji aplikacji
          </h1>
          <p className="font-body text-sm text-ink/70 mb-6">
            Brak połączenia z bazą danych. Skontaktuj się z administratorem.
          </p>
        </div>
      </div>
    )
  }

  // Ładowanie - z progressiv disclosure
  if (isLoading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <p className="font-ui text-sm text-ink/70 mb-4">
            {showLongLoading
              ? 'Ładowanie trwa dłużej niż zwykle...' 
              : 'Ładowanie...'}
          </p>
          
          {showLongLoading && (
            <div className="space-y-3">
              <p className="font-body text-xs text-ink/65">
                Możliwe przyczyny: problem z siecią, zablokowany localStorage, 
                lub skorumpowana sesja.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={retry}
                  className="font-ui text-xs px-4 py-2 border border-ink/20 text-ink hover:border-ochre hover:text-ochre transition-colors"
                  style={{ borderRadius: '1px' }}
                >
                  Spróbuj ponownie
                </button>
                <button
                  onClick={nukeSessionAndReload}
                  className="font-ui text-xs px-4 py-2 bg-burgundy/10 border border-burgundy/30 text-burgundy hover:bg-burgundy/20 transition-colors"
                  style={{ borderRadius: '1px' }}
                >
                  Wyczyść sesję i odśwież
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Błąd (ale nie ładowanie)
  if (error) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="font-display text-lg text-burgundy mb-2">
            Problem z logowaniem
          </h1>
          <p className="font-body text-sm text-ink/70 mb-4">
            {error}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={retry}
              className="font-ui text-sm px-4 py-2 border border-ink/20 text-ink hover:border-ochre hover:text-ochre transition-colors"
              style={{ borderRadius: '1px' }}
            >
              Spróbuj ponownie
            </button>
            <button
              onClick={nukeSessionAndReload}
              className="font-ui text-sm px-4 py-2 bg-burgundy/10 border border-burgundy/30 text-burgundy hover:bg-burgundy/20 transition-colors"
              style={{ borderRadius: '1px' }}
            >
              Wyczyść sesję i odśwież
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Wszystko OK - renderuj aplikację
  return <>{children}</>
}
